/**
 * 컬럼 토글 prefs 헬퍼 — 신규 default-on 컬럼이 옛 사용자에게도 자동으로
 * 보이도록 하는 표준 로직.
 *
 * 정책:
 *   - prefs 가 비어있음 (신규 사용자) → DEFAULT_COL_KEYS 그대로 활성.
 *   - prefs 가 있고 Known 필드도 있음 (정상) → 활성 목록 + (Known 에 없는 default-on 컬럼).
 *   - prefs 가 있지만 Known 필드 없음 (옛 사용자) → 활성 목록 + (활성 목록에 없는 default-on 컬럼).
 *     Known 미존재 = 마이그 전 → 활성 목록을 known 으로 추정.
 */

export function resolveActiveColumns(
  fromPrefs: string[] | undefined,
  knownInPrefs: string[] | undefined,
  allKeys: string[],
  defaultOnKeys: string[],
): Set<string> {
  // 신규 사용자 — prefs 비어있음 → default 그대로
  if (!Array.isArray(fromPrefs) || fromPrefs.length === 0) {
    return new Set(defaultOnKeys);
  }

  // 활성 목록 (현재 화면의 컬럼 정의에 존재하는 키만)
  const active = new Set(fromPrefs.filter((k) => allKeys.includes(k)));

  // Known 추정: prefs 의 known 필드가 있으면 그것, 없으면 활성 목록을 known 으로
  const known = Array.isArray(knownInPrefs) ? new Set(knownInPrefs) : active;

  // Known 에 없는 default-on 키 = 사용자가 본 적 없는 신규 컬럼 → 자동 활성
  for (const k of defaultOnKeys) {
    if (!known.has(k)) active.add(k);
  }

  return active;
}

/**
 * 📐 내 표 설정용 — 사용자가 지정한 컬럼 순서를 코드 상의 컬럼 정의에 맞춰 보정.
 *
 * - prefs 에 있는 키 중 현재 정의에 존재하는 키만 그 순서대로 앞에.
 * - 그 뒤에 prefs 에 없는 신규 키 (코드 순서 그대로) 를 붙임.
 * - prefs 미존재 또는 빈 배열이면 코드 순서 그대로 반환.
 */
export function resolveColumnOrder(
  fromPrefs: string[] | undefined,
  allKeys: string[],
): string[] {
  if (!Array.isArray(fromPrefs) || fromPrefs.length === 0) return [...allKeys];
  const allSet = new Set(allKeys);
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const k of fromPrefs) {
    if (allSet.has(k) && !seen.has(k)) {
      ordered.push(k);
      seen.add(k);
    }
  }
  for (const k of allKeys) {
    if (!seen.has(k)) ordered.push(k);
  }
  return ordered;
}
